import { AlertTitle, Typography } from "@mui/material";
import {
  CallToActionAlert,
  CallToActionAlertActionVariant,
} from "../alerts/CallToActionAlert";
import {
  CustomizationSectionType,
  CustomizationSourceType,
} from "../subscription/customize/hooks/useSections";

import useCustomizeQuery from "../newcustomize/hooks/useCustomizeQuery";
import { PlanIdTypeSafe } from "@/types/PlanTypes";
import usePlan from "@/hooks/usePlan";
import getProxyPlanKey from "@/utils/getProxyPlanKey";
import useBandwidth from "../newcustomize/ProxyDetails/BandWidth/hooks/useBandwidth";
import useMinCountForUnlimitedBandwidth from "../newcustomize/hooks/useMinProxyCountForUnlimitedBandwidth";

export default function BandwidthUsageBanner({
  usageState,
  source,
  planId,
  actionVariant,
}: {
  usageState: "error" | "warning";
  source: CustomizationSourceType;
  planId: PlanIdTypeSafe;
  actionVariant?: CallToActionAlertActionVariant;
}) {
  const { data: plan } = usePlan(planId);

  const {
    proxy_type,
    proxy_subtype,
    bandwidth_limit = 0,
    proxy_count = 0,
  } = plan || {};

  const isResidential = proxy_subtype === "residential";

  const proxyPlanKey = getProxyPlanKey({
    proxy_type: plan?.proxy_type || "shared",
    proxy_subtype: plan?.proxy_subtype || "default",
    proxy_count: plan?.proxy_count,
  });

  const { mapping: bandwidthMenuItems } = useBandwidth({
    onlyProxyPlan: proxyPlanKey,
  });

  const minCountForUnlimitedBandwidths = useMinCountForUnlimitedBandwidth();

  const minCountForUnlimitedBandwidth =
    minCountForUnlimitedBandwidths[proxyPlanKey];

  const maxBandwidth = Math.max(...bandwidthMenuItems.map((i) => i.bandwidth));
  const moreBandwidthNeedsMoreProxies =
    bandwidth_limit >= maxBandwidth &&
    proxy_count < (minCountForUnlimitedBandwidth || 0);

  const showCustomizeSections: CustomizationSectionType[] | undefined = (() => {
    switch (true) {
      case isResidential && moreBandwidthNeedsMoreProxies:
        return undefined;
      case moreBandwidthNeedsMoreProxies:
        return ["bandwidth-plan", "proxy-count"];
      default:
        return ["bandwidth-plan"];
    }
  })();

  const { link } = useCustomizeQuery({
    source,
    showCustomizeSections,
    existingPlanId: planId,
  });

  if (!proxy_type || !proxy_subtype || !plan) return null;
  if (
    typeof bandwidth_limit === "undefined" ||
    typeof proxy_count === "undefined"
  )
    return null;

  const nextBandwidth = bandwidthMenuItems.find(
    (i) => i.bandwidth > bandwidth_limit
  );

  const additionalQueryParams = (() => {
    switch (true) {
      case moreBandwidthNeedsMoreProxies:
        return {
          proxyCount: minCountForUnlimitedBandwidth,
          bandwidth: 0,
        };
      case isResidential:
        return undefined;
      default:
        return { bandwidth: nextBandwidth?.bandwidth || bandwidth_limit };
    }
  })();

  const search = { ...link.search, ...additionalQueryParams };

  return (
    <CallToActionAlert
      title="Increase Bandwidth"
      severity={usageState}
      actionVariant={actionVariant}
      link={
        proxy_type === "free"
          ? { to: "/subscription/choose-plan" }
          : { ...link, search }
      }
    >
      <BandwidthUsageCopy
        usageState={usageState}
        splitWarning={actionVariant === "text"}
      />
    </CallToActionAlert>
  );
}

const WARNING_TITLE = "Projected bandwidth usage is higher than the limit.";
const WARNING_DESCRIPTION =
  "You may exceed your bandwidth limit before the end of your subscription.";
const ERROR_TITLE =
  "You've used up all your bandwidth allowance and your proxies aren't working properly.";
const ERROR_DESCRIPTION =
  "To avoid interruptions in your proxy activity, you'll need to increase your bandwidth.";

/**
 * The Figma alert (dashboard product tabs) shows the warning as a title line
 * plus a body2 line, like the error already does. The original banner keeps
 * both warning sentences in the title so the experiment's control variant and
 * the stats page are unchanged.
 */
function BandwidthUsageCopy({
  usageState,
  splitWarning,
}: {
  usageState: "error" | "warning";
  splitWarning: boolean;
}) {
  if (usageState === "warning" && !splitWarning) {
    return (
      <AlertTitle>
        {WARNING_TITLE} {WARNING_DESCRIPTION}
      </AlertTitle>
    );
  }
  const isWarning = usageState === "warning";
  return (
    <>
      <AlertTitle>{isWarning ? WARNING_TITLE : ERROR_TITLE}</AlertTitle>
      <Typography variant="body2">
        {isWarning ? WARNING_DESCRIPTION : ERROR_DESCRIPTION}
      </Typography>
    </>
  );
}
